import { emptyWorkspace, parseWorkspace } from './workspace';
import { readWorkspace, writeWorkspace } from './storage';
import type { Workspace } from '../types';

type Listener = () => void;

/**
 * A single mutable workspace with change notification.
 *
 * Views mutate through `update`, which notifies subscribers and schedules a
 * write to local storage. Text inputs bind to their field directly and call
 * `touch` instead, so typing does not re-render the screen underneath the
 * caret.
 */
export class WorkspaceStore {
  #workspace: Workspace;
  #listeners = new Set<Listener>();
  #saveTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(initial: Workspace) {
    this.#workspace = initial;
  }

  static restore(): WorkspaceStore {
    const raw = readWorkspace();
    if (raw === null) return new WorkspaceStore(emptyWorkspace());
    try {
      return new WorkspaceStore(parseWorkspace(raw));
    } catch {
      return new WorkspaceStore(emptyWorkspace());
    }
  }

  get workspace(): Workspace {
    return this.#workspace;
  }

  subscribe(fn: Listener): () => void {
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  }

  /** Mutate the workspace, then re-render and persist. */
  update(fn: (p: Workspace) => void): void {
    fn(this.#workspace);
    this.#emit();
    this.persist();
  }

  /** Persist without re-rendering — used while typing into a bound input. */
  touch(): void {
    this.persist();
  }

  /** Replace the whole workspace, e.g. after opening a file. */
  replace(next: Workspace): void {
    this.#workspace = next;
    this.#emit();
    this.persist();
  }

  reset(): void {
    this.replace(emptyWorkspace());
  }

  persist(): void {
    clearTimeout(this.#saveTimer);
    this.#saveTimer = setTimeout(() => writeWorkspace(this.#workspace), 300);
  }

  #emit(): void {
    for (const fn of this.#listeners) fn();
  }
}
